<template>
  <v-card outlined rounded="lg" class="pa-4" elevation="0">
    <v-row class="ma-0" v-if="!isResult">
      <v-col class="text-center" cols="12">
        <span>{{ $t('Período') }}</span>
        <div class="d-flex justify-center align-center mt-3">
          <v-menu
            ref="startDateMenu"
            :close-on-content-click="false"
            :return-value.sync="startDate"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                append-icon="mdi-calendar"
                class="primary-button"
                style="text-transform: none"
                :disabled="isResult"
                readonly
                v-bind="attrs"
                v-on="on"
              >
                {{ `${formatMonth(startDate)}` }}
              </v-btn>
            </template>
            <v-date-picker
              v-model="startDate"
              type="month"
              locale="pt-br"
              no-title
              scrollable
              @change="
                $refs.startDateMenu.save(startDate),
                  (filterProp.data = null),
                  list()
              "
            >
            </v-date-picker>
          </v-menu>

          <span class="mx-2">{{ $t('até') }}</span>

          <v-menu
            ref="endDateMenu"
            :close-on-content-click="false"
            :return-value.sync="endDate"
            transition="scale-transition"
            @change="list"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                append-icon="mdi-calendar"
                class="primary-button"
                style="text-transform: none"
                readonly
                v-bind="attrs"
                :disabled="isResult"
                v-on="on"
              >
                {{ `${formatMonth(endDate)}` }}
              </v-btn>
            </template>
            <v-date-picker
              v-model="endDate"
              type="month"
              locale="pt-br"
              no-title
              scrollable
              @change="
                $refs.endDateMenu.save(endDate),
                  (filterProp.data = null),
                  list()
              "
            >
            </v-date-picker>
          </v-menu>
        </div>
      </v-col>

      <v-col cols="12">
        <v-divider class="my-4"></v-divider>
      </v-col>
    </v-row>

    <v-alert
      type="warning"
      class="my-3 pa-1 text-center"
      v-if="data.dataByMonth.length == 0"
    >
      {{ $t('Nenhuma calibração encontrada') }}
    </v-alert>

    <div v-else>
      <v-row class="ma-0" justify="center">
        <v-col cols="12">
          <div class="d-flex justify-center">
            <BarChart
              :chart-data="chartData"
              :options="options"
              :key="chartKey"
            />
          </div>

          <div>
            <v-simple-table class="main-table" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left"></th>
                    <th class="text-left"></th>
                    <th
                      class="text-center"
                      v-for="month in data.dataByMonth.map((x) => x.month)"
                      :key="month"
                    >
                      {{ month }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style="max-width: 60px"></td>
                    <td style="max-width: 100px">
                      {{ $t('Interna - Solicitada x Concluída') }}
                    </td>
                    <td
                      v-for="item in data.dataByMonth"
                      :key="item.month"
                      class="text-center"
                    >
                      {{ item.internalCalibrationSchedule }} /
                      {{ item.internalCalibrationExecuted }} --
                      {{ item.percentage }}%
                    </td>
                  </tr>
                  <tr>
                    <td style="max-width: 60px"></td>
                    <td style="max-width: 100px">
                      {{ $t('Externa - Solicitada x Concluída') }}
                    </td>
                    <td
                      v-for="item in data.dataByMonth"
                      :key="item.month"
                      class="text-center"
                    >
                      {{ item.externalCalibrationSchedule }}
                      /
                      {{ item.externalCalibrationExecuted }}
                      --
                      {{ item.externalPercentage }}%
                    </td>
                  </tr>
                  <tr>
                    <td style="max-width: 60px"></td>
                    <td style="max-width: 100px">
                      {{ $t('Total - Solicitada x Concluída') }}
                    </td>
                    <td
                      v-for="item in data.dataByMonth"
                      :key="item.month"
                      class="text-center"
                    >
                      {{ item.totalCalibrationSchedule }}
                      /
                      {{ item.totalCalibrationExecuted }} --
                      {{ item.totalPercentage }}%
                    </td>
                  </tr>
                  <tr style="background: orange">
                    <td style="max-width: 60px"></td>
                    <td style="font-weight: bold; color: white !important">
                      {{ $t('Meta') }}
                    </td>
                    <td
                      style="color: white !important"
                      v-for="item in data.dataByMonth"
                      :key="item.month"
                      class="text-center"
                    >
                      {{ `${data.goal}%` }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-row class="ma-0">
      <v-col cols="12" v-if="isResult">
        <strong>{{ $t('Análise crítica') }}</strong>
        <p v-html="review"></p>
      </v-col>
      <v-col cols="12" v-else>
        <tiptap-vuetify
          v-model="filter.review"
          :extensions="extensions"
          :label="$t('Análise crítica')"
          :placeholder="
            $t('Digite sua análise crítica para o indicador abaixo')
          "
          :card-props="{ flat: true, outlined: true, rounded: true }"
          class="primary-input"
          outlined
          dense
          hide-details="auto"
        ></tiptap-vuetify>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import BarChart from '../../../../charts/barChart'
import ApiService from '../../../../../../../../services/ApiService'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  props: {
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    this.filter.startDate = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')

    this.filter.endDate = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.startDate = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')

    this.endDate = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  components: {
    TiptapVuetify,
    BarChart,
  },

  data() {
    return {
      chartKey: false,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      filter: {
        page: 1,
        pageSize: 7,
        institutionId: null,
        unidadeIds: [],
        startDate: null,
        endDate: null,
        review: '',
      },
      startDate: null,
      endDate: null,
      apiService: new ApiService(),
      loading: false,
      calibrationScheduleExecuted: undefined,
      page: 1,
      showChart: true,
      totalPages: 1,
      pageSize: 7,
      percent: 0,
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          labels: {
            usePointStyle: true,
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
      },
      chartData: {},
      calibrationSchedule: [],
      calibrationExecuted: [],
      internalCalibrationSchedule: [],
      internalcClibrationExecuted: [],
      labels: [],
      total: 0,
      data: {
        dataByMonth: [],
      },
    }
  },

  methods: {
    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    setInfos() {
      const model = {
        chart: '1.12 - Calibrações agendadas x executadas',
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    formatMonth(date) {
      return moment(date).locale(this.$i18n.locale).format('MMMM/YYYY')
    },

    async list() {
      if (this.filterProp.data) {
        const response = JSON.parse(this.filterProp.data)
        this.data = response.content

        this.fillchartData()
        return
      }

      this.setDates()
      this.filter.institutionId = this.institution.id
      this.filter.pageSize = 7
      this.filter.unidadeIds = this.unidades
      await this.apiService
        .post('reports/calibrationScheduleExecuted', this.filter)
        .then((response) => {
          this.data = response.content
        })
        .catch((err) => {})

      this.fillchartData()
    },

    fillchartData() {
      this.chartData = {
        labels: this.data.dataByMonth.map((x) => x.month),
        datasets: [
          {
            label: this.$t('Meta'),
            type: 'line',
            data: this.data.dataByMonth.map((x) => this.data.goal),
            borderColor: 'orange',
            backgroundColor: 'rgba(255,128,0,0.1)',
            pointRadius: 5,
            pointHoverRadius: 10,
          },
          {
            label: this.$t('Execução total'),
            data: this.data.dataByMonth.map((x) => x.totalPercentage),
            backgroundColor: '#aaa',
            pointRadius: 5,
            pointHoverRadius: 10,
          },
          {
            label: this.$t('Execução interna'),
            data: this.data.dataByMonth.map((x) => x.percentage),
            backgroundColor: '#bbcf20',
            pointRadius: 5,
            pointHoverRadius: 10,
          },
          {
            label: this.$t('Execução externa'),
            data: this.data.dataByMonth.map((x) => x.externalPercentage),
            backgroundColor: '#0288d1',
            pointRadius: 5,
            pointHoverRadius: 10,
          },
        ],
      }

      setTimeout(() => {
        this.chartKey = !this.chartKey
      }, 500)
    },
  },
  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },

    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
